import React, { useState, useEffect, useMemo } from 'react';
import { Upload, Button, message, Typography, List, Input, Checkbox, Switch } from 'antd';
import { 
  UploadOutlined, 
  DeleteOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { RootState } from '../../redux';
import { useSelector, useDispatch } from 'react-redux';
import { setSearchCompanies, setMatchCompanies, retriveCompanies } from '../../redux/analysisSlice';
import { CompanyModel } from '../../data/company';
import type { AppDispatch } from '../../redux';

const { TextArea } = Input;
const { Paragraph, Title } = Typography;

interface Props {
  uploadedFiles: File[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  onStartProcessing: () => void;
  isCandidateFlow?: boolean;
  promptCompany: string;
  promptCandidate: string;
  setPromptCompany: React.Dispatch<React.SetStateAction<string>>;
  setPromptCandidate: React.Dispatch<React.SetStateAction<string>>;
  onBack: () => void;
}

const UploadDocuments: React.FC<Props> = ({ 
  uploadedFiles, 
  setUploadedFiles, 
  onStartProcessing, 
  isCandidateFlow, 
  promptCompany, 
  promptCandidate,
  setPromptCandidate,
  setPromptCompany,
  onBack 
}) => {
  const { companies } = useSelector((state: RootState) => state.analysis);
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const dispatch = useDispatch<AppDispatch>();  
  const props: UploadProps = {
    name: 'file',
    multiple: true,
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.xlsm,.ppt,.pptx,.txt',
    beforeUpload: file => {
      setUploadedFiles(prev => [...prev, file]);
      return false;
    },
    onChange(info) {
      if (info.file.status !== 'uploading') {
        message.success(`${info.file.name} file added`);
      }
    },
    showUploadList: false,
  };

  useEffect(() => {
    if (companies.allCompanies.length === 0) {
      dispatch(retriveCompanies()).unwrap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showAllCompanies) {
      const updatedAll = companies.allCompanies.map(company => ({
        ...company,
        selected: true
      }));
      dispatch(setSearchCompanies(updatedAll));
    } else {
      const matchingNames = new Set(companies.matchingCompanies.map(c => c.name));
  
      const updatedAll = companies.allCompanies.map(company => ({
        ...company,
        selected: matchingNames.has(company.name)
      }));

      const updatedMatches = companies.matchingCompanies.map(company => ({
        ...company,
        selected: true
      }));

      dispatch(setMatchCompanies(updatedMatches));
  
      dispatch(setSearchCompanies(updatedAll));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllCompanies]);

  const removeFile = (file: File) => {
    setUploadedFiles(prev => prev.filter(f => f !== file));
    message.success(`${file.name} removed`);
  };

  const handleToggle = (company: CompanyModel, checked: boolean) => {
    const updatedAll = companies.allCompanies.map((c) =>
      c.name === company.name ? { ...c, selected: checked } : c
    );
  
    const updatedMatching = companies.matchingCompanies.map((c) =>
      c.name === company.name ? { ...c, selected: checked } : c
    );
  
    dispatch(setSearchCompanies(updatedAll));
    if (!showAllCompanies) dispatch(setMatchCompanies(updatedMatching));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleStartProcessing = () => {
    const hasText = !!promptCompany?.trim();
    const hasFiles = uploadedFiles.length > 0;

    if (!hasText && !hasFiles) {
      message.warning('Please enter a prompt or upload at least one file');
      return;
    }

    onStartProcessing();
  };  

  const actionButtons = () => {
    return (
      <div className="action-buttons">
        <Button
          icon={<ArrowLeftOutlined />}
          className="button button-secondary"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          type="primary"
          className="button button-primary"
          onClick={handleStartProcessing}
          disabled={uploadedFiles.length === 0 && (!promptCompany || promptCompany.trim() === '')}          
          icon={<ArrowRightOutlined />}
          style={{ borderRadius: 4 }}
        >
          Start Processing
        </Button>
      </div>
    )
  }

  const displayedCompanies = useMemo(() => {
    if (showAllCompanies) {
      return companies.allCompanies.map(c => ({
        ...c,
      }));
    } else {
      return companies.matchingCompanies;
    }
  }, [showAllCompanies, companies.allCompanies, companies.matchingCompanies]);
  
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Paragraph className="step-description">
          {isCandidateFlow ? "Describe type of role you are looking for" : "Describe type of construction project or expertise you are looking for"}
        </Paragraph>
        <TextArea
          rows={5}
          placeholder="e.g., Companies experienced in renovating cultural heritage buildings..."
          value={isCandidateFlow ? promptCandidate : promptCompany}
          onChange={e => isCandidateFlow ? setPromptCandidate(e.target.value) : setPromptCompany(e.target.value)}
          style={{ fontSize: 14 }}
        />
      </div>

      {!isCandidateFlow && (
        <>
          <div style={{ marginBottom: 12 }}>
            <Upload
              {...props}
              showUploadList={false}
            >
              <Button
                type="link"
                icon={<UploadOutlined />}
                style={{ padding: 0 }}
              >
                Add supporting documents
              </Button>
            </Upload>
          </div>
          
          {uploadedFiles.length > 0 && (
            <div className="file-list" style={{ marginBottom: 16 }}>
              <List
                itemLayout="horizontal"
                size="small"
                dataSource={uploadedFiles}
                renderItem={(file: File) => (
                  <List.Item
                    className="file-item"
                    actions={[
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeFile(file)}
                        className="file-action-btn"
                      />
                    ]}
                  >
                    <div className="file-name">{file.name}</div>
                    <div className="file-meta" style={{ fontSize: 12, color: '#888' }}>
                      {formatFileSize(file.size)}
                    </div>
                  </List.Item>
                )}
              />
            </div>
          )}
        </>
      )}

      {isCandidateFlow && companies.matchingCompanies && companies.matchingCompanies.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Title level={5}>Choose Between Retrieved Companies or All Portfolio Companies</Title>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <span style={{ marginRight: 8, fontWeight: 500 }}>Retrieved from Previous Step</span>
              <Switch
                checked={showAllCompanies}
                onChange={setShowAllCompanies}
              />
              <span style={{ marginLeft: 8, fontWeight: 500 }}>All Portfolio Companies</span>
            </div>
            {actionButtons()}
          </div>
          
          <div
            style={{
              maxHeight: 300,
              overflowY: 'auto',
              border: '1px solid #f0f0f0',
              padding: 12,
              borderRadius: 4,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '8px 16px',
              marginTop: "10px"
            }}
          >
            {displayedCompanies.map((company) => (
              <Checkbox
                key={company._id}
                checked={company.selected}
                onChange={(e) => handleToggle(company, e.target.checked)}
              >
                {company.name}
              </Checkbox>
            ))}
          </div>
        </div>
      )}

      {!isCandidateFlow && actionButtons()}
    </>
  );
};

export default UploadDocuments;